import React, { useState, useEffect } from "react";
import { Search, FileText, Printer, Mail, Eye, Loader2, X, RefreshCw, CreditCard, User, Plus, Trash2, Calculator, Pencil } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const DocumentLookup = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  // Payment-method change (inline edit)
  const [editingPaymentIdx, setEditingPaymentIdx] = useState(null);
  const [editingPaymentValue, setEditingPaymentValue] = useState("");
  const [savingPaymentEdit, setSavingPaymentEdit] = useState(false);

  // Salesperson change / void modal state
  const [salespersonModalOpen, setSalespersonModalOpen] = useState(false);
  const [salespersonList, setSalespersonList] = useState([]);
  const [salespersonChoice, setSalespersonChoice] = useState("");  // "" | "clear" | "<id>"
  const [savingSalesperson, setSavingSalesperson] = useState(false);
  const [voidModalOpen, setVoidModalOpen] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [savingVoid, setSavingVoid] = useState(false);

  // Company settings (for VAT display)
  const [isVatRegistered, setIsVatRegistered] = useState(false);
  const [hasVatCompanyProfile, setHasVatCompanyProfile] = useState(false);

  // VAT invoice generation
  const [generatingVatInvoice, setGeneratingVatInvoice] = useState(false);
  const [vatInvoiceResult, setVatInvoiceResult] = useState(null);
  const [vatEmailModalOpen, setVatEmailModalOpen] = useState(false);
  const [vatEmailAddress, setVatEmailAddress] = useState("");
  const [sendingVatEmail, setSendingVatEmail] = useState(false);

  // Payment conversion state
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertTo, setConvertTo] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [converting, setConverting] = useState(false);

  // Dummy Invoice state
  const [dummyModalOpen, setDummyModalOpen] = useState(false);
  const [vatRecomputing, setVatRecomputing] = useState(false);
  const [dummyCustomer, setDummyCustomer] = useState("");
  const [dummyNotes, setDummyNotes] = useState("");
  const [dummyItems, setDummyItems] = useState([{ name: "", plu_code: "", quantity: 1, price: 0 }]);
  const [creatingDummy, setCreatingDummy] = useState(false);
  const [dummyProductSearch, setDummyProductSearch] = useState("");
  const [dummySearchResults, setDummySearchResults] = useState([]);
  const [dummySearchIndex, setDummySearchIndex] = useState(null);

  // ── SARS Document Reprint ──────────────────────────────────────────────
  // SARS audit-friendly reprint of Cash/Card/Account invoices. No VAT shown.
  // Lookup one invoice number at a time and stack them into a batch, then
  // download a single combined PDF or print directly.
  const [sarsInvoiceInput, setSarsInvoiceInput] = useState("");
  const [sarsLookingUp, setSarsLookingUp] = useState(false);
  const [sarsBatch, setSarsBatch] = useState([]);  // [{ invoice_number, …data }]
  const [sarsError, setSarsError] = useState("");
  const [sarsCandidates, setSarsCandidates] = useState(null);  // {message, candidates[]} | null
  const [sarsPicking, setSarsPicking] = useState(false);

  const handleSarsLookup = async (overrideInternalId = null, invOverride = null) => {
    const inv = (invOverride ?? sarsInvoiceInput).trim();
    if (!inv) {
      toast.error("Enter an invoice number");
      return;
    }
    if (!overrideInternalId && sarsBatch.some(b => b.invoice_number === inv)) {
      toast.info(`Invoice ${inv} is already in the batch`);
      setSarsInvoiceInput("");
      return;
    }
    setSarsLookingUp(true);
    setSarsError("");
    try {
      const params = overrideInternalId ? { internal_id: overrideInternalId } : {};
      const r = await axios.get(
        `${API}/api/documents/sars-reprint/${encodeURIComponent(inv)}`,
        { params }
      );
      if (sarsBatch.some(b => b.invoice_number === r.data.invoice_number)) {
        toast.info(`Invoice ${r.data.invoice_number} is already in the batch`);
      } else {
        setSarsBatch(prev => [...prev, r.data]);
        toast.success(`Added ${r.data.invoice_number} to batch`);
      }
      setSarsInvoiceInput("");
      setSarsCandidates(null);
    } catch (err) {
      const detail = err.response?.data?.detail;
      // 409 from backend = multiple candidates — open picker.
      if (err.response?.status === 409 && detail && typeof detail === "object" && Array.isArray(detail.candidates)) {
        setSarsCandidates(detail);
        setSarsError("");
      } else {
        const msg = typeof detail === "string" ? detail : (err.message || "Lookup failed");
        setSarsError(msg);
        toast.error(msg);
      }
    } finally {
      setSarsLookingUp(false);
    }
  };

  const handleSarsPickCandidate = async (cand) => {
    setSarsPicking(true);
    try {
      await handleSarsLookup(cand.internal_id);
    } finally {
      setSarsPicking(false);
    }
  };

  const handleSarsRemove = (invNum) => {
    setSarsBatch(prev => prev.filter(b => b.invoice_number !== invNum));
  };

  const handleSarsClear = () => {
    setSarsBatch([]);
    setSarsError("");
  };

  // Build the SARS-compliant HTML for the batch and either print or PDF-export.
  // ONE template for every invoice → satisfies the "all invoices look the same"
  // SARS requirement.
  const buildSarsBatchHtml = () => {
    const fmtR = (n) => `R ${Number(n || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const escapeHtml = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const escapeAddr = (s) => escapeHtml(s).replace(/\n/g, "<br/>");
    const fmtDate = (d) => {
      if (!d) return "";
      try {
        const dt = new Date(d);
        if (isNaN(dt)) return String(d);
        return dt.toLocaleDateString("en-ZA", { year: "numeric", month: "short", day: "2-digit" });
      } catch {
        return String(d);
      }
    };
    const pages = sarsBatch.map((inv, idx) => `
      <section class="invoice-page ${idx === sarsBatch.length - 1 ? "last" : ""}">
        <header class="hdr">
          <div class="company">
            <div class="cname">${escapeHtml(inv.company.name)}</div>
            ${inv.company.address ? `<div class="caddr">${escapeAddr(inv.company.address)}</div>` : ""}
            <div class="cmeta">
              ${inv.company.phone ? `Tel: ${escapeHtml(inv.company.phone)}<br/>` : ""}
              ${inv.company.email ? `Email: ${escapeHtml(inv.company.email)}<br/>` : ""}
              Reg No: <strong>${escapeHtml(inv.company.registration_number || "—")}</strong>
            </div>
          </div>
          <div class="invbox">
            <div class="invtitle">INVOICE</div>
            <div class="invmeta"><span>Invoice No:</span> <strong>${escapeHtml(inv.invoice_number)}</strong></div>
            <div class="invmeta"><span>Date:</span> <strong>${escapeHtml(fmtDate(inv.date))}</strong></div>
          </div>
        </header>

        <div class="customer-block">
          <div class="cust-label">BILL TO</div>
          <div class="cust-name">${escapeHtml(inv.customer.name)}</div>
          <div class="cust-addr">${escapeAddr(inv.customer.address)}</div>
          ${inv.customer.registration_number ? `<div class="cust-reg">Reg No: <strong>${escapeHtml(inv.customer.registration_number)}</strong></div>` : ""}
          ${inv.customer.phone ? `<div class="cust-phone">Tel: ${escapeHtml(inv.customer.phone)}</div>` : ""}
        </div>

        <table class="items">
          <thead>
            <tr>
              <th class="th-plu">PLU</th>
              <th class="th-desc">Description</th>
              <th class="th-qty">Qty</th>
              <th class="th-price">Unit Price</th>
              <th class="th-total">Line Total</th>
            </tr>
          </thead>
          <tbody>
            ${(inv.items || []).map(it => `
              <tr>
                <td>${escapeHtml(it.plu_code || "")}</td>
                <td>${escapeHtml(it.name || "")}</td>
                <td class="num">${Number(it.quantity || 0).toLocaleString("en-ZA")}</td>
                <td class="num">${fmtR(it.unit_price)}</td>
                <td class="num">${fmtR(it.line_total)}</td>
              </tr>
            `).join("")}
          </tbody>
        </table>

        <div class="totals">
          ${Number(inv.discount || 0) > 0 ? `<div class="row"><span>Discount</span><span>- ${fmtR(inv.discount)}</span></div>` : ""}
          <div class="row grand"><span>TOTAL</span><span>${fmtR(inv.total)}</span></div>
        </div>

        <footer class="ftr">
          Thank you for your business. &nbsp;·&nbsp; ${escapeHtml(inv.company.name)} &nbsp;·&nbsp; Reg ${escapeHtml(inv.company.registration_number || "—")}
        </footer>
      </section>
    `).join("");

    return `<!doctype html>
<html><head><meta charset="utf-8"/>
<title>SARS Reprint — ${sarsBatch.map(i => i.invoice_number).join(", ")}</title>
<style>
  @page { size: A4 portrait; margin: 15mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #111; margin: 0; font-size: 11pt; }
  .invoice-page { padding: 0; page-break-after: always; }
  .invoice-page.last { page-break-after: auto; }
  .hdr { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #111; padding-bottom: 10px; margin-bottom: 16px; }
  .company .cname { font-size: 18pt; font-weight: 900; letter-spacing: 0.5px; }
  .company .caddr { font-size: 10pt; line-height: 1.4; margin-top: 4px; white-space: pre-line; }
  .company .cmeta { font-size: 9.5pt; line-height: 1.5; margin-top: 6px; color: #333; }
  .invbox { text-align: right; }
  .invtitle { font-size: 16pt; font-weight: 900; letter-spacing: 1px; border: 2px solid #111; padding: 4px 10px; display: inline-block; }
  .invmeta { font-size: 10.5pt; margin-top: 6px; }
  .invmeta span { color: #555; }
  .customer-block { margin-bottom: 16px; padding: 10px 12px; border: 1px solid #ccc; background: #fafafa; }
  .cust-label { font-size: 8.5pt; font-weight: 700; letter-spacing: 1px; color: #666; margin-bottom: 4px; }
  .cust-name { font-size: 12pt; font-weight: 700; }
  .cust-addr { font-size: 10pt; line-height: 1.4; margin-top: 2px; }
  .cust-reg { font-size: 9.5pt; color: #333; margin-top: 4px; }
  .cust-phone { font-size: 9.5pt; color: #444; margin-top: 4px; }
  table.items { width: 100%; border-collapse: collapse; margin-bottom: 14px; }
  table.items th { background: #111; color: #fff; padding: 6px 8px; text-align: left; font-size: 9.5pt; letter-spacing: 0.4px; }
  table.items th.th-qty, table.items th.th-price, table.items th.th-total { text-align: right; }
  table.items td { padding: 6px 8px; border-bottom: 1px solid #e5e5e5; font-size: 10pt; vertical-align: top; }
  table.items td.num { text-align: right; font-variant-numeric: tabular-nums; }
  .totals { margin-left: auto; width: 45%; }
  .totals .row { display: flex; justify-content: space-between; padding: 4px 0; font-size: 10.5pt; }
  .totals .row.grand { border-top: 2px solid #111; margin-top: 6px; padding-top: 8px; font-size: 13pt; font-weight: 900; }
  .ftr { margin-top: 28px; padding-top: 8px; border-top: 1px solid #ccc; text-align: center; color: #666; font-size: 8.5pt; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style></head>
<body>${pages}</body></html>`;
  };

  const handleSarsPrint = () => {
    if (sarsBatch.length === 0) {
      toast.error("Add at least one invoice to the batch first");
      return;
    }
    const html = buildSarsBatchHtml();
    const w = window.open("", "_blank", "width=900,height=1000");
    if (!w) { toast.error("Pop-up blocked. Please allow pop-ups."); return; }
    w.document.write(html);
    w.document.close();
    setTimeout(() => { try { w.print(); } catch { /* user cancelled */ } }, 400);
  };

  const handleSarsDownloadPdf = () => {
    // Browsers don't give a JS API for "Save as PDF" without a library, so we
    // open the same print window and tell the user to pick the PDF printer.
    handleSarsPrint();
    setTimeout(() => {
      toast.info("In the Print dialog, choose 'Save as PDF' as the destination.", { duration: 6000 });
    }, 500);
  };

  // Load customers and company settings on mount
  useEffect(() => {
    // Auto-launch the SARS lookup if we arrived from the Historical Invoices
    // page (it sets ?sars=<invoice_number>).
    try {
      const sp = new URLSearchParams(window.location.search);
      const presetInv = sp.get("sars");
      if (presetInv) {
        setSarsInvoiceInput(presetInv);
        // Clear the param so a refresh doesn't re-trigger.
        const url = new URL(window.location.href);
        url.searchParams.delete("sars");
        window.history.replaceState({}, "", url.pathname + url.search);
        // Defer the lookup one tick so state has propagated.
        setTimeout(() => {
          // eslint-disable-next-line no-use-before-define
          handleSarsLookup(null, presetInv);
        }, 50);
      }
    } catch (e) { /* ignore */ }

    const loadCustomers = async () => {
      try {
        const response = await axios.get(`${API}/api/customers`, {
          params: { include_calculated_balance: true, limit: 3000 }  // Use calculated balance
        });
        setCustomers(response.data || []);
      } catch (error) {
        console.error("Failed to load customers:", error);
      }
    };

    const loadCompanySettings = async () => {
      try {
        const response = await axios.get(`${API}/api/settings/company`);
        const s = response.data;
        setIsVatRegistered(!!(s.vat_enabled !== false && s.vat_number));
      } catch (error) {
        console.error("Failed to load company settings:", error);
        setIsVatRegistered(false);
      }
    };

    const loadVatCompanyProfile = async () => {
      try {
        const response = await axios.get(`${API}/api/settings/vat-company`);
        setHasVatCompanyProfile(!!(response.data?.vat_number));
      } catch (error) {
        setHasVatCompanyProfile(false);
      }
    };

    loadCustomers();
    loadCompanySettings();
    loadVatCompanyProfile();
  }, []);

  const handleRecomputeVat = async () => {
    setVatRecomputing(true);
    try {
      // Step 1: dry-run to preview the impact
      const previewResp = await axios.post(`${API}/api/documents/vat-invoices/recompute-totals`, { dry_run: true });
      const { scanned, changed_count, total_vat_correction } = previewResp.data || {};

      if (!changed_count) {
        toast.success(`All ${scanned} existing VAT invoices already SARS-correct. Nothing to fix.`);
        setVatRecomputing(false);
        return;
      }

      const sign = total_vat_correction > 0 ? "+" : "";
      const ok = window.confirm(
        `Scanned ${scanned} existing VAT invoices.\n\n` +
        `${changed_count} need correcting — VAT will change by ${sign}R ${Number(total_vat_correction).toFixed(2)} in total.\n` +
        `(Negative means VAT was overcharged on zero-rated items like bread/cooking oil — that VAT will now be removed.)\n\n` +
        `Apply the corrections now? This rewrites the invoice document's subtotal, tax and zero-rated breakdown.`
      );
      if (!ok) {
        toast("Cancelled — no changes made.");
        setVatRecomputing(false);
        return;
      }

      // Step 2: apply
      const applyResp = await axios.post(`${API}/api/documents/vat-invoices/recompute-totals`, { dry_run: false });
      toast.success(
        `Corrected ${applyResp.data.changed_count} VAT invoice(s). Total VAT change: R ${Number(applyResp.data.total_vat_correction).toFixed(2)}.`
      );
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Failed to recompute VAT totals");
    } finally {
      setVatRecomputing(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery || searchQuery.length < 2) {
      toast.error("Please enter at least 2 characters to search");
      return;
    }

    setSearching(true);
    try {
      const response = await axios.get(`${API}/api/documents/search?q=${encodeURIComponent(searchQuery)}`);
      setResults(response.data.results || []);
      
      if (response.data.results.length === 0) {
        toast.info("No documents found matching your search");
      } else {
        toast.success(`Found ${response.data.results.length} document(s)`);
      }
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Failed to search documents");
    } finally {
      setSearching(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const viewDocument = async (doc) => {
    try {
      const response = await axios.get(`${API}/api/documents/${doc.collection}/${doc.id}`);
      // doc first (search-list summary) so the full document fields take precedence —
      // critical for status/voided_at/voided_by/void_reason which the search list
      // hard-codes to "Completed".
      setSelectedDocument({ ...doc, ...response.data.document });
      setShowDetailsModal(true);
    } catch (error) {
      console.error("Error loading document:", error);
      toast.error("Failed to load document details");
    }
  };

  const reprintDocument = async (doc) => {
    // PDF endpoints per document collection.
    // For unsupported types we fall back to the old "open detail view" flow
    // and surface a helpful toast — better than printing the dialog itself.
    const PDF_ENDPOINTS = {
      sales: (id) => `${API}/api/documents/sales/${id}/pdf`,
      vat_invoices: (id) => `${API}/api/documents/vat_invoices/${id}/pdf`,
      purchase_orders: (id) => `${API}/api/documents/purchase_orders/${id}/pdf`,
      quotations: (id) => `${API}/api/documents/quotations/${id}/pdf`,
    };

    const buildUrl = PDF_ENDPOINTS[doc.collection];
    if (!buildUrl) {
      toast.error(
        `Reprint not yet supported for ${doc.collection.replace("_", " ")}. ` +
        `Use the View button to inspect the document.`
      );
      return;
    }

    toast.info("Preparing document for printing…");
    try {
      // Fetch the PDF as a blob so we can pop it into a new tab and trigger
      // the browser's native print dialog. This guarantees we print the
      // actual document — never the screen behind it.
      const response = await axios.get(buildUrl(doc.id), { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      const popup = window.open(blobUrl, "_blank");
      if (!popup) {
        toast.error("Pop-up blocked. Allow pop-ups for this site and try again.");
        window.URL.revokeObjectURL(blobUrl);
        return;
      }
      // Once the PDF is loaded in the new tab, kick off print and free the URL.
      const triggerPrint = () => {
        try { popup.focus(); popup.print(); } catch { /* user cancelled */ }
      };
      // Some browsers (Safari) need the load event; others (Chrome) need a small delay.
      popup.addEventListener?.("load", triggerPrint, { once: true });
      setTimeout(triggerPrint, 1200);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
      toast.success("Document sent to printer");
    } catch (error) {
      console.error("Reprint error:", error);
      const detail = error.response?.data?.detail || error.message || "Unknown error";
      toast.error(`Failed to reprint: ${detail}`);
    }
  };

  const emailDocument = async (doc) => {
    setSelectedDocument(doc);
    // Pre-fill customer email if available
    let email = doc.customer_email || "";
    if (!email && doc.customer_id) {
      try {
        const res = await axios.get(`${API}/api/customers/${doc.customer_id}`);
        email = res.data?.email || "";
      } catch { /* ignore */ }
    }
    setEmailAddress(email);
    setEmailModalOpen(true);
  };

  const sendEmail = async () => {
    if (!emailAddress || !emailAddress.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    setSendingEmail(true);
    try {
      await axios.post(`${API}/api/documents/email`, {
        document_type: selectedDocument.collection,
        document_id: selectedDocument.id,
        email: emailAddress
      });
      
      toast.success(`Document emailed to ${emailAddress}`);
      setEmailModalOpen(false);
    } catch (error) {
      console.error("Email error:", error);
      toast.error("Failed to email document");
    } finally {
      setSendingEmail(false);
    }
  };

  const sendVatEmail = async () => {
    if (!vatEmailAddress || !vatEmailAddress.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    setSendingVatEmail(true);
    try {
      await axios.post(`${API}/api/documents/vat-invoice/email`, {
        vat_invoice_id: vatInvoiceResult.vat_invoice_id,
        email: vatEmailAddress,
      });
      toast.success(`VAT Invoice emailed to ${vatEmailAddress}`);
      setVatEmailModalOpen(false);
      setVatEmailAddress("");
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to send email";
      toast.error(msg);
    } finally {
      setSendingVatEmail(false);
    }
  };

  const generateVatInvoice = async (doc) => {
    if (!hasVatCompanyProfile) {
      toast.error("VAT company profile not set up. Go to Settings → VAT Company to configure it first.");
      return;
    }
    setGeneratingVatInvoice(true);
    setVatInvoiceResult(null);
    try {
      const response = await axios.post(`${API}/api/documents/convert-to-vat-invoice`, {
        source_invoice_id: doc.id,
        source_collection: doc.collection,
      });
      setVatInvoiceResult(response.data);
      toast.success(`VAT Invoice ${response.data.vat_invoice_number} created!`);
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to generate VAT invoice";
      toast.error(msg);
    } finally {
      setGeneratingVatInvoice(false);
    }
  };

  const downloadVatInvoicePdf = async (vatInvoiceId) => {
    try {
      const response = await axios.get(`${API}/api/documents/vat_invoices/${vatInvoiceId}/pdf`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${vatInvoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download VAT invoice PDF");
    }
  };

  // Payment type conversion
  const openConvertModal = (doc) => {
    setSelectedDocument(doc);
    setConvertTo("");
    setSelectedCustomer(null);
    setCustomerSearch("");
    setShowConvertModal(true);
  };

  const handleConvertPayment = async () => {
    if (!convertTo) {
      toast.error("Please select a payment type to convert to");
      return;
    }

    if ((convertTo === "ACCOUNT" || convertTo === "CUSTOMER") && !selectedCustomer) {
      toast.error("Please select a customer first");
      return;
    }

    setConverting(true);
    try {
      const response = await axios.post(`${API}/api/sales/convert-payment-type`, {
        transaction_number: selectedDocument.document_number,
        convert_to: convertTo,
        customer_id: selectedCustomer?.id,
        customer_name: selectedCustomer?.name,
        reason: "Cashier correction via Document Lookup"
      });

      toast.success(`Payment type converted successfully!`);
      response.data.changes?.forEach(change => toast.info(change));
      
      setShowConvertModal(false);
      setShowDetailsModal(false);
      
      // Refresh search results
      handleSearch();
    } catch (error) {
      console.error("Conversion error:", error);
      toast.error(error.response?.data?.detail || "Failed to convert payment type");
    } finally {
      setConverting(false);
    }
  };

  const filteredCustomers = customers.filter(c => 
    c.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.account_number?.toLowerCase().includes(customerSearch.toLowerCase())
  ).slice(0, 10);

  const getDocumentTypeColor = (type) => {
    const colors = {
      "Invoice/Sale": "bg-blue-100 text-blue-800",
      "Receipt": "bg-green-100 text-green-800",
      "Refund": "bg-red-100 text-red-800",
      "GRV": "bg-purple-100 text-purple-800",
      "Purchase Order": "bg-yellow-100 text-yellow-800",
      "Quotation": "bg-gray-100 text-gray-800"
    };
    return colors[type] || "bg-gray-100 text-gray-800";
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR"
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-ZA", {
        year: "numeric",
        month: "short",
        day: "numeric"
      });
    } catch {
      return dateString;
    }
  };


  // Dummy Invoice functions
  const handleDummyProductSearch = async (query, index) => {
    setDummySearchIndex(index);
    setDummyProductSearch(query);
    if (query.length < 2) { setDummySearchResults([]); return; }
    try {
      const res = await axios.get(`${API}/api/products/search?q=${encodeURIComponent(query)}&limit=8`);
      setDummySearchResults(res.data.products || res.data || []);
    } catch { setDummySearchResults([]); }
  };

  const selectDummyProduct = (product, index) => {
    const updated = [...dummyItems];
    updated[index] = {
      ...updated[index],
      name: product.name,
      plu_code: product.plu_code || "",
      price: product.selling_price_incl_vat || 0,
    };
    setDummyItems(updated);
    setDummySearchResults([]);
    setDummyProductSearch("");
    setDummySearchIndex(null);
  };

  const addDummyItem = () => {
    setDummyItems([...dummyItems, { name: "", plu_code: "", quantity: 1, price: 0 }]);
  };

  const removeDummyItem = (index) => {
    setDummyItems(dummyItems.filter((_, i) => i !== index));
  };

  const updateDummyItem = (index, field, value) => {
    const updated = [...dummyItems];
    updated[index] = { ...updated[index], [field]: value };
    setDummyItems(updated);
  };

  const createDummyInvoice = async () => {
    const validItems = dummyItems.filter(i => i.name && i.price > 0);
    if (validItems.length === 0) { toast.error("Add at least one item with a price"); return; }
    setCreatingDummy(true);
    try {
      const res = await axios.post(`${API}/api/documents/dummy-invoice`, {
        customer_name: dummyCustomer || "CASH",
        items: validItems,
        notes: dummyNotes,
      });
      toast.success(`Dummy Invoice ${res.data.invoice_number} created!`);
      // Print it
      printDummyInvoice(res.data);
      setDummyModalOpen(false);
      setDummyItems([{ name: "", plu_code: "", quantity: 1, price: 0 }]);
      setDummyCustomer("");
      setDummyNotes("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create dummy invoice");
    } finally {
      setCreatingDummy(false);
    }
  };

  const printDummyInvoice = (invoice) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) { toast.error("Pop-up blocked"); return; }
    
    const itemsHtml = invoice.items.map(item => `
      <tr>
        <td style="padding:4px 0;border-bottom:1px solid #eee;">${item.name}</td>
        <td style="padding:4px 8px;border-bottom:1px solid #eee;text-align:center;">${item.quantity}</td>
        <td style="padding:4px 8px;border-bottom:1px solid #eee;text-align:right;">R${item.price.toFixed(2)}</td>
        <td style="padding:4px 0;border-bottom:1px solid #eee;text-align:right;font-weight:bold;">R${item.total.toFixed(2)}</td>
      </tr>
    `).join('');

    printWindow.document.write(`
      <!DOCTYPE html><html><head>
        <style>
          body { font-family: Arial, sans-serif; max-width: 700px; margin: 20px auto; padding: 20px; }
          .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
          .header h1 { margin: 0; font-size: 24px; }
          .header p { margin: 4px 0; color: #555; }
          .watermark { color: #d00; font-weight: bold; text-align: center; font-size: 14px; border: 2px solid #d00; padding: 5px; margin: 10px 0; }
          table { width: 100%; border-collapse: collapse; }
          th { text-align: left; border-bottom: 2px solid #333; padding: 6px 0; }
          .totals { margin-top: 10px; text-align: right; }
          .totals td { padding: 3px 0; }
          .total-row { font-size: 18px; font-weight: bold; border-top: 2px solid #333; }
          @media print { .no-print { display: none; } }
        </style>
      </head><body>
        <div class="header">
          <h1>BOSVELD BOU & HARDWARE</h1>
          <p>R572, Farm Van Wyksfontein, Tom Burke | 083 278 5937</p>
        </div>
        <div class="watermark">*** DUMMY INVOICE - NOT A TAX DOCUMENT ***</div>
        <div style="display:flex;justify-content:space-between;margin:15px 0;">
          <div><strong>Invoice:</strong> ${invoice.invoice_number}<br><strong>Customer:</strong> ${invoice.customer_name}</div>
          <div style="text-align:right;"><strong>Date:</strong> ${invoice.date}<br>${invoice.notes ? `<strong>Notes:</strong> ${invoice.notes}` : ''}</div>
        </div>
        <table>
          <thead><tr><th>Description</th><th style="text-align:center;">Qty</th><th style="text-align:right;">Price</th><th style="text-align:right;">Total</th></tr></thead>
          <tbody>${itemsHtml}</tbody>
        </table>
        <table class="totals">
          <tr><td>Excl. VAT:</td><td style="padding-left:20px;">R${invoice.excl_total.toFixed(2)}</td></tr>
          <tr><td>VAT (15%):</td><td style="padding-left:20px;">R${invoice.vat_amount.toFixed(2)}</td></tr>
          <tr class="total-row"><td>TOTAL:</td><td style="padding-left:20px;">R${invoice.total.toFixed(2)}</td></tr>
        </table>
        <div class="watermark" style="margin-top:20px;">*** THIS IS NOT A VALID TAX INVOICE - FOR REFERENCE ONLY ***</div>
        <script>window.onload = function() { window.print(); }</script>
      </body></html>
    `);
    printWindow.document.close();
  };


  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* ── SARS Document Reprint ─────────────────────────────────────── */}
      <Card className="border-amber-300 bg-amber-50/30" data-testid="sars-reprint-card">
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-amber-900">
                <FileText className="w-6 h-6" />
                SARS Document Reprint
              </CardTitle>
              <CardDescription className="text-amber-800/80 mt-1">
                Audit-ready invoice reprint. Identical layout for every invoice, full company &amp;
                customer address, company registration number, <strong>NO VAT</strong> shown.
                Cash / Card / Account invoices only — refunds &amp; voids are blocked.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              className="border-amber-400 text-amber-900 hover:bg-amber-100 shrink-0"
              onClick={() => window.location.href = "/historical-invoices"}
              data-testid="goto-historical-invoices-btn"
            >
              <FileText className="w-4 h-4 mr-1" /> Historical Invoices
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              value={sarsInvoiceInput}
              onChange={(e) => setSarsInvoiceInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleSarsLookup(); }}
              placeholder="Enter invoice number (e.g. INV-001234 or 1234)"
              className="flex-1"
              data-testid="sars-invoice-input"
            />
            <Button
              onClick={() => handleSarsLookup()}
              disabled={sarsLookingUp}
              className="bg-amber-700 hover:bg-amber-800 text-white"
              data-testid="sars-lookup-btn"
            >
              {sarsLookingUp ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Looking up…</>
              ) : (
                <><Plus className="w-4 h-4 mr-1" /> Add to batch</>
              )}
            </Button>
          </div>
          {sarsError && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="sars-error">
              {sarsError}
            </div>
          )}
          {sarsCandidates && (
            <div className="text-sm bg-blue-50 border border-blue-200 rounded p-3 space-y-2" data-testid="sars-picker">
              <div className="font-medium text-blue-900">
                {sarsCandidates.message} Click one to add it.
              </div>
              <div className="space-y-1">
                {sarsCandidates.candidates.map((c) => (
                  <button
                    key={c.internal_id}
                    type="button"
                    disabled={sarsPicking}
                    onClick={() => handleSarsPickCandidate(c)}
                    className="w-full text-left p-2 rounded border border-blue-300 bg-white hover:bg-blue-100 transition flex justify-between items-center gap-3 disabled:opacity-60"
                    data-testid={`sars-candidate-${c.internal_id}`}
                  >
                    <div className="flex flex-col">
                      <span className="font-mono font-semibold">{c.invoice_number}</span>
                      <span className="text-xs text-gray-600">
                        {c.transaction_type || "—"} · {c.customer_name || "(no customer)"}
                        {c.date ? ` · ${new Date(c.date).toLocaleDateString("en-ZA")}` : ""}
                      </span>
                    </div>
                    <span className="font-mono">R {Number(c.total || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2 })}</span>
                  </button>
                ))}
              </div>
              <Button variant="ghost" size="sm" onClick={() => setSarsCandidates(null)} data-testid="sars-picker-cancel">
                Cancel
              </Button>
            </div>
          )}
          {sarsBatch.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-amber-900">Batch ({sarsBatch.length}):</span>
                <Button variant="ghost" size="sm" onClick={handleSarsClear} data-testid="sars-clear-btn">
                  <Trash2 className="w-4 h-4 mr-1" /> Clear all
                </Button>
              </div>
              <div className="border border-amber-200 rounded bg-white overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-amber-100/60">
                      <TableHead className="w-32">Invoice #</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sarsBatch.map((b) => (
                      <TableRow key={b.invoice_number} data-testid={`sars-batch-row-${b.invoice_number}`}>
                        <TableCell className="font-mono font-semibold">{b.invoice_number}</TableCell>
                        <TableCell>{b.customer?.name || "—"}</TableCell>
                        <TableCell className="text-sm text-gray-600">{(b.items || []).length} line(s)</TableCell>
                        <TableCell className="text-right font-mono">
                          R {Number(b.total || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleSarsRemove(b.invoice_number)}
                            data-testid={`sars-remove-${b.invoice_number}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button
                  onClick={handleSarsPrint}
                  className="bg-amber-700 hover:bg-amber-800 text-white"
                  data-testid="sars-print-btn"
                >
                  <Printer className="w-4 h-4 mr-1" /> Print batch (A4)
                </Button>
                <Button
                  variant="outline"
                  onClick={handleSarsDownloadPdf}
                  className="border-amber-700 text-amber-800 hover:bg-amber-50"
                  data-testid="sars-pdf-btn"
                >
                  <FileText className="w-4 h-4 mr-1" /> Download combined PDF
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Universal Document Lookup (existing) ───────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-6 h-6" />
            Universal Document Lookup
          </CardTitle>
          <CardDescription className="flex items-center justify-between gap-2 flex-wrap">
            <span>Search for any document by number: invoices, receipts, refunds, GRVs, purchase orders, quotations</span>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                onClick={handleRecomputeVat}
                variant="outline"
                size="sm"
                disabled={vatRecomputing}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="recompute-vat-btn"
                title="Recompute totals on existing VAT invoices so zero-rated items (bread, cooking oil etc.) no longer carry 15% VAT."
              >
                {vatRecomputing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    Recomputing…
                  </>
                ) : (
                  <>
                    <Calculator className="h-4 w-4 mr-1" />
                    Fix VAT on old invoices
                  </>
                )}
              </Button>
              <Button
                onClick={() => setDummyModalOpen(true)}
                variant="outline"
                size="sm"
                className="border-orange-300 text-orange-700 hover:bg-orange-50"
                data-testid="create-dummy-invoice-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Dummy Invoice
              </Button>
            </div>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Search Bar */}
          <div className="flex gap-2 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Enter invoice #, receipt #, GRV #, or customer's PO/order #"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                className="pl-10"
              />
            </div>
            <Button onClick={handleSearch} disabled={searching}>
              {searching ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Search
                </>
              )}
            </Button>
          </div>

          {/* Results Table */}
          {results.length > 0 && (
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Document #</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Customer/Supplier</TableHead>
                    <TableHead>Customer PO #</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {results.map((doc, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Badge className={getDocumentTypeColor(doc.document_type)}>
                          {doc.document_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">{doc.document_number}</TableCell>
                      <TableCell>{formatDate(doc.date)}</TableCell>
                      <TableCell>{doc.customer_name}</TableCell>
                      <TableCell className="font-mono text-xs text-gray-700">
                        {doc.customer_order_number || "—"}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(doc.total)}
                      </TableCell>
                      <TableCell>{doc.status}</TableCell>
                      <TableCell>
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => viewDocument(doc)}
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {doc.document_type === "Invoice/Sale" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openConvertModal(doc)}
                              title="Convert Payment Type"
                              className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                          )}
                          {doc.can_reprint && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => reprintDocument(doc)}
                              title="Reprint"
                            >
                              <Printer className="w-4 h-4" />
                            </Button>
                          )}
                          {doc.can_email && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => emailDocument(doc)}
                              title="Email"
                            >
                              <Mail className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Empty State */}
          {!searching && results.length === 0 && searchQuery && (
            <div className="text-center py-12 text-gray-500">
              <FileText className="w-16 h-16 mx-auto mb-4 opacity-20" />
              <p className="text-lg font-medium">No documents found</p>
              <p className="text-sm">Try searching with a different document number</p>
            </div>
          )}

          {/* Initial State */}
          {!searching && results.length === 0 && !searchQuery && (
            <div className="text-center py-12 text-gray-500">
              <Search className="w-16 h-16 mx-auto mb-4 opacity-20" />
              <p className="text-lg font-medium">Enter a document number to search</p>
              <p className="text-sm">Search across all document types in your system</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Document Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={(open) => { setShowDetailsModal(open); if (!open) { setVatInvoiceResult(null); setVatEmailAddress(""); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Document Details</span>
              <div className="flex gap-2 flex-wrap">
                {selectedDocument?.can_reprint && (
                  <Button size="sm" variant="outline" onClick={() => reprintDocument(selectedDocument)}>
                    <Printer className="w-4 h-4 mr-1" /> Reprint
                  </Button>
                )}
                {selectedDocument?.can_email && (
                  <Button size="sm" variant="outline" onClick={() => emailDocument(selectedDocument)}>
                    <Mail className="w-4 h-4 mr-1" /> Email
                  </Button>
                )}
                {/* "Generate VAT Invoice" button removed — Bosveld now has
                    its own VAT number, so every till slip is already a valid
                    SARS tax invoice. Historical VITEX VAT invoices can still
                    be looked up and reprinted directly from Document Lookup. */}
              </div>
            </DialogTitle>
            <DialogDescription>
              {selectedDocument?.document_type} - {selectedDocument?.document_number}
            </DialogDescription>
          </DialogHeader>

          {/* VAT Invoice Result Banner */}
          {vatInvoiceResult && (
            <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
              <div>
                <span className="font-semibold text-blue-800">VAT Invoice created: </span>
                <span className="text-blue-700">{vatInvoiceResult.vat_invoice_number}</span>
                <span className="text-blue-600 ml-2">— Search for it in Document Lookup</span>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-blue-600 text-blue-700"
                  onClick={async () => {
                    let email = selectedDocument?.customer_email || "";
                    if (!email && selectedDocument?.customer_id) {
                      try {
                        const res = await axios.get(`${API}/api/customers/${selectedDocument.customer_id}`);
                        email = res.data?.email || "";
                      } catch { /* ignore */ }
                    }
                    setVatEmailAddress(email);
                    setVatEmailModalOpen(true);
                  }}
                  data-testid="email-vat-invoice-btn"
                >
                  <Mail className="w-4 h-4 mr-1" /> Email
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-blue-600 text-blue-700"
                  onClick={() => downloadVatInvoicePdf(vatInvoiceResult.vat_invoice_id)}
                  data-testid="download-vat-pdf-btn"
                >
                  <FileText className="w-4 h-4 mr-1" /> Download PDF
                </Button>
              </div>
            </div>
          )}
          {selectedDocument && (
            <div className="space-y-4">
              {/* Summary Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">Date</p>
                  <p className="font-medium">
                    {formatDate(selectedDocument.sale_date || selectedDocument.date || selectedDocument.created_at || selectedDocument.order_date)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Amount</p>
                  <p className="font-medium text-lg">{formatCurrency(selectedDocument.total)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Customer/Supplier</p>
                  <p className="font-medium">{selectedDocument.customer_name || selectedDocument.supplier_name || "Walk-in"}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Type</p>
                  <Badge className={getDocumentTypeColor(selectedDocument.document_type)}>
                    {selectedDocument.transaction_type || selectedDocument.document_type}
                  </Badge>
                </div>
              </div>

              {/* Items Table - for sales, refunds, GRVs */}
              {selectedDocument.items && selectedDocument.items.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Items ({selectedDocument.items.length})</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead>Product</TableHead>
                          <TableHead className="text-right">Qty</TableHead>
                          <TableHead className="text-right">Unit Price</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedDocument.items.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{item.name || item.description || item.product_name}</p>
                                {item.plu_code && <p className="text-xs text-gray-500">PLU: {item.plu_code}</p>}
                                {item.price_override_reason && (
                                  <p className="text-xs text-amber-700" data-testid={`doc-price-override-${idx}`}>
                                    Price lowered{item.price_override_original ? ` from ${formatCurrency(item.price_override_original)}` : ""} — {item.price_override_reason}
                                  </p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">{item.quantity}</TableCell>
                            <TableCell className="text-right">{formatCurrency(item.unit_price || item.price)}</TableCell>
                            <TableCell className="text-right font-medium">{formatCurrency(item.total || (item.quantity * (item.unit_price || item.price || 0)))}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Payment Details - for sales (with inline payment-method edit).
                  For account receipts (RCP) we synthesise a single-row payments
                  view so the same edit UI works. */}
              {(() => {
                const isAccountReceipt =
                  selectedDocument.collection === 'account_payments' ||
                  (selectedDocument.document_number || '').startsWith('RCP-') ||
                  (selectedDocument.receipt_number || '').startsWith('RCP-');
                const displayPayments =
                  (selectedDocument.payments && selectedDocument.payments.length > 0)
                    ? selectedDocument.payments
                    : (isAccountReceipt
                      ? [{ method: selectedDocument.payment_method || 'CASH', amount: selectedDocument.amount ?? selectedDocument.total }]
                      : []);
                if (displayPayments.length === 0) return null;
                return (
                <div>
                  <h4 className="font-semibold mb-2">Payment Details</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {displayPayments.map((payment, idx) => {
                      const isEditing = editingPaymentIdx === idx;
                      const canEdit = (selectedDocument.collection === 'sales' || isAccountReceipt);
                      return (
                        <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded gap-2">
                          {isEditing ? (
                            <>
                              <Select value={editingPaymentValue} onValueChange={setEditingPaymentValue}>
                                <SelectTrigger className="h-8 text-xs" data-testid={`payment-method-select-${idx}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="CASH">CASH</SelectItem>
                                  <SelectItem value="CARD">CARD</SelectItem>
                                  <SelectItem value="EFT">EFT</SelectItem>
                                  <SelectItem value="CREDIT">CREDIT (Account)</SelectItem>
                                  <SelectItem value="CHEQUE">CHEQUE</SelectItem>
                                </SelectContent>
                              </Select>
                              <div className="flex items-center gap-1">
                                <Button
                                  size="sm"
                                  variant="default"
                                  className="h-7 px-2"
                                  disabled={savingPaymentEdit}
                                  data-testid={`payment-method-save-${idx}`}
                                  onClick={async () => {
                                    if (!editingPaymentValue) return;
                                    setSavingPaymentEdit(true);
                                    try {
                                      const collectionUsed = isAccountReceipt ? 'account_payments' : 'sales';
                                      await axios.put(
                                        `${API}/api/documents/${collectionUsed}/${selectedDocument.id}/payment-method`,
                                        {
                                          new_method: editingPaymentValue,
                                          payment_index: isAccountReceipt ? null : idx,
                                        }
                                      );
                                      const newPayments = [...displayPayments];
                                      newPayments[idx] = { ...newPayments[idx], method: editingPaymentValue };
                                      setSelectedDocument({
                                        ...selectedDocument,
                                        payments: selectedDocument.payments ? newPayments : selectedDocument.payments,
                                        payment_method: editingPaymentValue,
                                      });
                                      setEditingPaymentIdx(null);
                                      toast.success(`Payment method changed to ${editingPaymentValue}`);
                                    } catch (err) {
                                      toast.error(err?.response?.data?.detail || "Failed to update payment method");
                                    } finally {
                                      setSavingPaymentEdit(false);
                                    }
                                  }}
                                >Save</Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2"
                                  disabled={savingPaymentEdit}
                                  data-testid={`payment-method-cancel-${idx}`}
                                  onClick={() => setEditingPaymentIdx(null)}
                                >Cancel</Button>
                              </div>
                            </>
                          ) : (
                            <>
                              <span className="flex items-center gap-2">
                                {payment.method}
                                {canEdit && (
                                  <button
                                    type="button"
                                    onClick={() => { setEditingPaymentIdx(idx); setEditingPaymentValue(payment.method || ""); }}
                                    className="text-blue-600 hover:text-blue-800"
                                    title="Change payment method"
                                    data-testid={`payment-method-edit-${idx}`}
                                  >
                                    <Pencil className="h-3 w-3" />
                                  </button>
                                )}
                              </span>
                              <span className="font-medium">{formatCurrency(payment.amount)}</span>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                );
              })()}

              {/* Totals Summary */}
              <div className="border-t pt-4">
                <div className="flex flex-col items-end space-y-1">
                  {isVatRegistered && selectedDocument.subtotal && (
                    <div className="flex justify-between w-48">
                      <span className="text-gray-500">Subtotal:</span>
                      <span>{formatCurrency(selectedDocument.subtotal)}</span>
                    </div>
                  )}
                  {selectedDocument.discount > 0 && (
                    <div className="flex justify-between w-48 text-red-600">
                      <span>Discount:</span>
                      <span>-{formatCurrency(selectedDocument.discount)}</span>
                    </div>
                  )}
                  {isVatRegistered && selectedDocument.tax > 0 && (
                    <div className="flex justify-between w-48">
                      <span className="text-gray-500">VAT (15%):</span>
                      <span>{formatCurrency(selectedDocument.tax)}</span>
                    </div>
                  )}
                  <div className="flex justify-between w-48 text-lg font-bold border-t pt-1">
                    <span>Total:</span>
                    <span>{formatCurrency(selectedDocument.total)}</span>
                  </div>
                </div>
              </div>

              {/* SALESPERSON ATTRIBUTION + VOID — only for sales documents */}
              {selectedDocument.collection === "sales" && (
                <div className="border-t pt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Salesperson</p>
                      <p className="font-medium" data-testid="sale-salesperson-name">
                        {selectedDocument.salesperson_name
                          ? `${selectedDocument.salesperson_name}${selectedDocument.salesperson_marketing_number ? ` (#${selectedDocument.salesperson_marketing_number})` : ""}`
                          : "Counter Sale (no salesperson)"}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {selectedDocument.status !== "voided" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            try {
                              const res = await axios.get(`${API}/api/employees/salespeople`);
                              setSalespersonList(res.data || []);
                            } catch { /* ignore */ }
                            setSalespersonChoice("");
                            setSalespersonModalOpen(true);
                          }}
                          data-testid="open-change-salesperson"
                        >
                          Change Salesperson
                        </Button>
                      )}
                      {selectedDocument.status !== "voided" && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => { setVoidReason(""); setVoidModalOpen(true); }}
                          data-testid="open-void-sale"
                        >
                          Void Sale
                        </Button>
                      )}
                      {selectedDocument.status === "voided" && (
                        <div className="flex items-center gap-2">
                          <Badge variant="destructive">VOIDED — {selectedDocument.void_reason}</Badge>
                          {(() => {
                            const voidedAt = selectedDocument.voided_at ? new Date(selectedDocument.voided_at) : null;
                            const withinWindow = voidedAt && (Date.now() - voidedAt.getTime()) < 24 * 60 * 60 * 1000;
                            if (!withinWindow) return null;
                            return (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-amber-700 border-amber-400 hover:bg-amber-50"
                                onClick={async () => {
                                  if (!window.confirm("Unvoid this sale? It will be restored and counted in reports again.")) return;
                                  try {
                                    await axios.post(
                                      `${API}/api/documents/sales/${selectedDocument.id}/unvoid`,
                                      { changed_by: "Admin" },
                                    );
                                    setSelectedDocument({
                                      ...selectedDocument,
                                      status: "completed",
                                      void_reason: undefined,
                                      voided_at: undefined,
                                    });
                                    toast.success("Sale restored");
                                  } catch (err) {
                                    toast.error(err?.response?.data?.detail || "Unvoid failed");
                                  }
                                }}
                                data-testid="open-unvoid-sale"
                              >
                                Unvoid
                              </Button>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Info */}
              {(selectedDocument.cashier || selectedDocument.notes) && (
                <div className="text-sm text-gray-500 border-t pt-4">
                  {selectedDocument.cashier && <p>Cashier: {selectedDocument.cashier}</p>}
                  {selectedDocument.notes && <p>Notes: {selectedDocument.notes}</p>}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Modal */}
      <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Email Document</DialogTitle>
            <DialogDescription>
              Send {selectedDocument?.document_type} {selectedDocument?.document_number} via email
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Email Address</label>
              <Input
                type="email"
                placeholder="customer@example.com"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                className="mt-1"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setEmailModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={sendEmail} disabled={sendingEmail}>
                {sendingEmail ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Send Email
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* VAT Invoice Email Modal */}
      <Dialog open={vatEmailModalOpen} onOpenChange={setVatEmailModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Email VAT Invoice</DialogTitle>
            <DialogDescription>
              Send {vatInvoiceResult?.vat_invoice_number} as a PDF attachment
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Customer Email Address</label>
              <Input
                type="email"
                placeholder="customer@example.com"
                value={vatEmailAddress}
                onChange={(e) => setVatEmailAddress(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendVatEmail()}
                className="mt-1"
                data-testid="vat-email-input"
                autoFocus
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setVatEmailModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={sendVatEmail} disabled={sendingVatEmail} data-testid="send-vat-email-btn">
                {sendingVatEmail ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                ) : (
                  <><Mail className="w-4 h-4 mr-2" />Send VAT Invoice</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Type Conversion Modal */}
      <Dialog open={showConvertModal} onOpenChange={setShowConvertModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-orange-500" />
              Convert Payment Type
            </DialogTitle>
            <DialogDescription>
              Change the payment type for {selectedDocument?.document_number}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Current Info */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Current Payment Type</p>
              <p className="font-semibold">{selectedDocument?.transaction_type || "Unknown"}</p>
              <p className="text-sm text-gray-500 mt-2">Amount</p>
              <p className="font-semibold text-lg">{formatCurrency(selectedDocument?.total)}</p>
            </div>

            {/* Convert To Selection */}
            <div>
              <Label>Convert To</Label>
              <Select value={convertTo} onValueChange={setConvertTo}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select new payment type..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CARD">
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4" />
                      Card Sale
                    </div>
                  </SelectItem>
                  <SelectItem value="CASH">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">💵</span>
                      Cash Sale
                    </div>
                  </SelectItem>
                  <SelectItem value="CUSTOMER">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Customer Sale (cash/card, link customer)
                    </div>
                  </SelectItem>
                  <SelectItem value="ACCOUNT">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Account Sale
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Customer Selection (for Account OR Customer Sale) */}
            {(convertTo === "ACCOUNT" || convertTo === "CUSTOMER") && (
              <div>
                <Label>Select Customer *</Label>
                <Input
                  placeholder="Search customer..."
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  className="mt-1"
                  data-testid="convert-customer-search-input"
                />
                {customerSearch && filteredCustomers.length > 0 && (
                  <div className="border rounded-lg mt-2 max-h-40 overflow-y-auto">
                    {filteredCustomers.map((customer) => (
                      <div
                        key={customer.id}
                        className={`p-2 cursor-pointer hover:bg-blue-50 ${
                          selectedCustomer?.id === customer.id ? "bg-blue-100" : ""
                        }`}
                        onClick={() => {
                          setSelectedCustomer(customer);
                          setCustomerSearch(customer.name);
                        }}
                        data-testid={`convert-customer-option-${customer.id}`}
                      >
                        <p className="font-medium">{customer.name}</p>
                        <p className="text-xs text-gray-500">
                          {customer.account_number} | Balance: {formatCurrency(customer.calculated_balance ?? customer.account_balance)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                {selectedCustomer && (
                  <div className="mt-2 p-2 bg-blue-50 rounded-lg">
                    <p className="text-sm font-medium text-blue-800">Selected: {selectedCustomer.name}</p>
                    {convertTo === "ACCOUNT" ? (
                      <p className="text-xs text-blue-600">
                        Current Balance: {formatCurrency(selectedCustomer.calculated_balance ?? selectedCustomer.account_balance)} → 
                        New Balance: {formatCurrency((selectedCustomer.calculated_balance ?? selectedCustomer.account_balance ?? 0) + (selectedDocument?.total || 0))}
                      </p>
                    ) : (
                      <p className="text-xs text-blue-600">
                        Sale will be linked to this customer. Payment method (cash/card) stays the same — customer balance is NOT charged.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Warning for Account to Card/Cash */}
            {convertTo && convertTo !== "ACCOUNT" && selectedDocument?.transaction_type?.includes("ACCOUNT") && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  ⚠️ This will <strong>credit</strong> the customer's account by {formatCurrency(selectedDocument?.total)}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleConvertPayment} 
              disabled={converting || !convertTo || ((convertTo === "ACCOUNT" || convertTo === "CUSTOMER") && !selectedCustomer)}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {converting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Converting...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Convert Payment
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dummy Invoice Dialog */}
      <Dialog open={dummyModalOpen} onOpenChange={setDummyModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-700">
              <FileText className="h-5 w-5" />
              Create Dummy Invoice
            </DialogTitle>
            <p className="text-xs text-orange-600 bg-orange-50 border border-orange-200 rounded p-2 mt-2">
              This invoice is for reference only. It will NOT affect stock levels or financial records.
            </p>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* Customer */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Customer Name</Label>
                <Input
                  value={dummyCustomer}
                  onChange={(e) => setDummyCustomer(e.target.value)}
                  placeholder="CASH"
                  data-testid="dummy-customer-input"
                />
              </div>
              <div>
                <Label className="text-sm">Notes (optional)</Label>
                <Input
                  value={dummyNotes}
                  onChange={(e) => setDummyNotes(e.target.value)}
                  placeholder="e.g. For insurance quote"
                  data-testid="dummy-notes-input"
                />
              </div>
            </div>

            {/* Items */}
            <div>
              <Label className="text-sm mb-2 block">Items</Label>
              <div className="space-y-2">
                {dummyItems.map((item, index) => (
                  <div key={index} className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                    <div className="flex-1 relative">
                      <Input
                        value={item.name}
                        onChange={(e) => {
                          updateDummyItem(index, "name", e.target.value);
                          handleDummyProductSearch(e.target.value, index);
                        }}
                        placeholder="Product name (type to search)"
                        className="text-sm"
                      />
                      {dummySearchResults.length > 0 && dummySearchIndex === index && (
                        <div className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-40 overflow-auto">
                          {dummySearchResults.map(p => (
                            <button
                              key={p.id}
                              onClick={() => selectDummyProduct(p, index)}
                              className="w-full text-left px-3 py-1.5 hover:bg-blue-50 text-sm border-b last:border-0"
                            >
                              <span className="font-medium">{p.name}</span>
                              <span className="text-xs text-gray-400 ml-2">R{p.selling_price_incl_vat?.toFixed(2)}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      value={item.quantity}
                      onChange={(e) => updateDummyItem(index, "quantity", parseFloat(e.target.value) || 1)}
                      className="w-20 text-sm text-center"
                      placeholder="Qty"
                    />
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.price || ""}
                      onChange={(e) => updateDummyItem(index, "price", parseFloat(e.target.value) || 0)}
                      className="w-28 text-sm text-right"
                      placeholder="Price"
                    />
                    <span className="w-24 text-right text-sm font-mono font-medium">
                      R{(item.quantity * item.price).toFixed(2)}
                    </span>
                    {dummyItems.length > 1 && (
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500" onClick={() => removeDummyItem(index)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" className="mt-2" onClick={addDummyItem}>
                <Plus className="h-3 w-3 mr-1" /> Add Item
              </Button>
            </div>

            {/* Total */}
            <div className="flex justify-end pt-2 border-t">
              <div className="text-right">
                <div className="text-sm text-gray-500">
                  Total: <span className="text-lg font-bold text-gray-900 ml-2">
                    R{dummyItems.reduce((sum, i) => sum + (i.quantity * i.price), 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDummyModalOpen(false)}>Cancel</Button>
            <Button
              onClick={createDummyInvoice}
              disabled={creatingDummy}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="create-dummy-btn"
            >
              {creatingDummy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Printer className="h-4 w-4 mr-2" />}
              Create & Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Salesperson Modal */}
      <Dialog open={salespersonModalOpen} onOpenChange={setSalespersonModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Change Salesperson</DialogTitle>
            <DialogDescription>
              Re-attribute this sale to a different salesperson, or clear it back to Counter Sale.
              Every change is logged for the commission audit trail.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="text-sm font-medium">Pick salesperson</label>
            <select
              value={salespersonChoice}
              onChange={(e) => setSalespersonChoice(e.target.value)}
              className="w-full border rounded p-2"
              data-testid="salesperson-change-select"
            >
              <option value="">— Select —</option>
              <option value="clear">Counter Sale (clear)</option>
              {salespersonList.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} {s.marketing_number ? `(#${s.marketing_number})` : ""}
                </option>
              ))}
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSalespersonModalOpen(false)} disabled={savingSalesperson}>Cancel</Button>
            <Button
              disabled={!salespersonChoice || savingSalesperson}
              onClick={async () => {
                setSavingSalesperson(true);
                try {
                  const body = salespersonChoice === "clear"
                    ? { clear: true, changed_by: "Admin" }
                    : { salesperson_id: salespersonChoice, changed_by: "Admin" };
                  const res = await axios.put(
                    `${API}/api/documents/sales/${selectedDocument.id}/salesperson`,
                    body,
                  );
                  setSelectedDocument({
                    ...selectedDocument,
                    salesperson_id: res.data.new?.id,
                    salesperson_name: res.data.new?.name,
                    salesperson_marketing_number: res.data.new?.marketing_number,
                  });
                  toast.success(salespersonChoice === "clear" ? "Cleared to Counter Sale" : "Salesperson updated");
                  setSalespersonModalOpen(false);
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Failed to update salesperson");
                } finally {
                  setSavingSalesperson(false);
                }
              }}
              data-testid="save-salesperson-change"
            >
              {savingSalesperson ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Void Sale Modal */}
      <Dialog open={voidModalOpen} onOpenChange={setVoidModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-700">Void Sale</DialogTitle>
            <DialogDescription>
              This will mark the sale as voided. It stays in the database for the audit trail but is
              excluded from financial reports, the leaderboard, and commission calculations.
              <span className="block mt-1 font-semibold">This cannot be undone from the UI.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">Reason (required)</label>
            <Input
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              placeholder="e.g. Captured wrong customer; duplicate ring-up"
              data-testid="void-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoidModalOpen(false)} disabled={savingVoid}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={!voidReason.trim() || savingVoid}
              onClick={async () => {
                setSavingVoid(true);
                try {
                  await axios.post(
                    `${API}/api/documents/sales/${selectedDocument.id}/void`,
                    { reason: voidReason.trim(), changed_by: "Admin" },
                  );
                  setSelectedDocument({ ...selectedDocument, status: "voided", void_reason: voidReason.trim() });
                  toast.success("Sale voided");
                  setVoidModalOpen(false);
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Failed to void sale");
                } finally {
                  setSavingVoid(false);
                }
              }}
              data-testid="confirm-void-sale"
            >
              {savingVoid ? "Voiding..." : "Void Sale"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentLookup;
